import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Select, Spin, Row, Col, Radio } from "antd";
import moment from "moment";
import CanvasJSReact from "../../../../../assets/canvasjs.react";

import { Container } from "./styles";

import { useFinancial } from "../../../../../hooks/useFinancialReducer";
import { useMemo } from "react";
import RadioButton from "../../../../../components/utils/radioButton";

// Modifica os separadores de milhar e decimal
CanvasJSReact.CanvasJS.addCultureInfo("br", {
  decimalSeparator: ",",
  digitGroupSeparator: ".",
});

const { CanvasJSChart } = CanvasJSReact;

const TIME_SERIES_TYPE = ["year", "harvest"];

const StackedColumnBarChart = (props) => {
  const { translation } = useSelector((state) => state.app);

  const [listCostType] = useState([
    { costType: "Growth", dataIndex: "growthTotalAccomplished" },
    {
      costType: "RecreateFattenPasture",
      dataIndex: "recreateFattenPastureTotalAccomplished",
    },
    {
      costType: "RecreateFattenSemiConfinement",
      dataIndex: "recreateFattenSemiConfinementTotalAccomplished",
    },
    {
      costType: "RecreateFattenConfinement",
      dataIndex: "recreateFattenConfinementTotalAccomplished",
    },
    { costType: "Agriculture", dataIndex: "agricultureTotalAccomplished" },
    { costType: "Others", dataIndex: "othersTotalAccomplished" },
  ]);

  const [costType, setCostType] = useState("AllTypes");
  const [listHarvests, setListHarvests] = useState([]);
  const [harvestSelected, setHarvestSelected] = useState(null);

  const [expenseData, setExpenseData] = useState([]);
  const [incomeData, setIncomeData] = useState([]);
  const [resultData, setResultData] = useState([]);
  const [balanceData, setBalanceData] = useState([]);

  const [timeSeriesType, setTimeSeriesType] = useState("harvest");
  const [selectedYear, setSelectedYear] = useState(moment().format("YYYY"));

  // Redux
  const {
    listFinancialCashFlow: cashFlowData,
    isLoadingListFinancialCashFlow,
  } = useFinancial();

  const renderYears = useMemo(() => {
    if (listHarvests.length > 0) {
      const yearsStart = listHarvests.map((h) => {
        return moment(h.startDate).format("YYYY");
      });
      const yearsEnd = listHarvests.map((h) => {
        return moment(h.endDate).format("YYYY");
      });
      const uniqueYears = [...new Set(yearsStart.concat(yearsEnd))].sort(
        (a, b) => a - b
      );
      return uniqueYears;
    }
    return [moment().format("YYYY")];
  }, [listHarvests]);

  useEffect(() => {
    function loadHarvest() {
      if (
        Array.isArray(cashFlowData?.harvests) &&
        cashFlowData.harvests.length > 0
      ) {
        setListHarvests(cashFlowData.harvests);
        const harvestId =
          cashFlowData.harvests.find((h) => h.current === true)?.id ??
          cashFlowData.harvests[0].id;
        setHarvestSelected(harvestId ?? null);
      }
    }

    if (cashFlowData !== null && cashFlowData !== undefined) {
      loadHarvest();
    }
  }, [cashFlowData]);

  const options = {
    culture: "br",
    animationEnabled: true,
    exportEnabled: false,
    axisY: {
      valueFormatString: "#,##0.00",
      includeZero: true,
      gridDashType: "dot",
    },
    toolTip: {
      shared: true,
      reversed: false,
    },
    legend: {
      verticalAlign: "center",
      horizontalAlign: "right",
      reversed: true,
      cursor: "pointer",
      itemclick: (e) => toggleDataSeries(e),
    },
    data: [
      {
        type: "stackedColumn",
        name: "Despesas",
        showInLegend: true,
        yValueFormatString: "#,##0.00",
        color: "#d44c4c",
        dataPoints: expenseData,
      },
      {
        type: "stackedColumn",
        name: "Receitas",
        showInLegend: true,
        yValueFormatString: "#,##0.00",
        color: "#33c162",
        dataPoints: incomeData,
      },
      {
        type: "line",
        name: "Resultado",
        showInLegend: true,
        color: "#166AAB",
        yValueFormatString: "#,##0.00",
        dataPoints: resultData,
      },
      {
        type: "line",
        name: "Saldo",
        showInLegend: true,
        color: "#4b4b4b",
        yValueFormatString: "#,##0.00",
        dataPoints: balanceData,
      },
    ],
  };

  const toggleDataSeries = (e) => {
    if (typeof e.dataSeries.visible === "undefined" || e.dataSeries.visible) {
      e.dataSeries.visible = false;
    } else {
      e.dataSeries.visible = true;
    }
    e.chart.render();
  };

  useEffect(() => {
    function generateChart() {
      if (
        Array.isArray(cashFlowData?.valuesGroupByNature) &&
        cashFlowData.valuesGroupByNature.length > 0
      ) {
        const startDate =
          timeSeriesType === "year"
            ? moment(`${selectedYear}-01-01`)
            : listHarvests.find((h) => h.id === harvestSelected)?.startDate;
        const endDate =
          timeSeriesType === "year"
            ? moment(`${selectedYear}-12-31`)
            : listHarvests.find((h) => h.id === harvestSelected)?.endDate;

        const expenseList = cashFlowData.valuesGroupByNature.find(
          (cf) => cf.natureType === "expense" && cf.natureName === "Total"
        )?.values;
        const incomeList = cashFlowData.valuesGroupByNature.find(
          (cf) => cf.natureType === "income" && cf.natureName === "Total"
        )?.values;

        // Filter expenses by harvest
        let expensesInPeriod = expenseList.filter((v) =>
          moment(v.movementDate).isBetween(
            moment(startDate),
            moment(endDate),
            "days",
            "[]"
          )
        );

        // Filter incomes by harvest
        let incomesInPeriod = incomeList.filter((v) =>
          moment(v.movementDate).isBetween(
            moment(startDate),
            moment(endDate),
            "days",
            "[]"
          )
        );

        if (timeSeriesType === "year") {
          const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
          expensesInPeriod = months.map((month) => {
            const monthFormatted = month < 10 ? `0${month}` : month;
            const monthStart = moment(`${selectedYear}-${monthFormatted}-01`);
            const indexFounded = expensesInPeriod.findIndex((v) =>
              moment(v.movementDate).isSame(monthStart, "month")
            );
            if (indexFounded !== -1) {
              return expensesInPeriod[indexFounded];
            }
            return {
              movementDate: monthStart,
              allTypesTotalForeseen: 0,
              allTypesTotalAccomplished: 0,
              growthTotalForeseen: 0,
              growthTotalAccomplished: 0,
              recreateFattenPastureTotalForeseen: 0,
              recreateFattenPastureTotalAccomplished: 0,
              recreateFattenSemiConfinementTotalForeseen: 0,
              recreateFattenSemiConfinementTotalAccomplished: 0,
              recreateFattenConfinementTotalForeseen: 0,
              recreateFattenConfinementTotalAccomplished: 0,
              agricultureTotalForeseen: 0,
              agricultureTotalAccomplished: 0,
              othersTotalForeseen: 0,
              othersTotalAccomplished: 0,
              allTypesTotalForeseenNotSettled: 0,
              allTypesTotalAccomplishedNotSettled: 0,
              growthTotalForeseenNotSettled: 0,
              growthTotalAccomplishedNotSettled: 0,
              recreateFattenPastureTotalForeseenNotSettled: 0,
              recreateFattenPastureTotalAccomplishedNotSettled: 0,
              recreateFattenSemiConfinementTotalForeseenNotSettled: 0,
              recreateFattenSemiConfinementTotalAccomplishedNotSettled: 0,
              recreateFattenConfinementTotalForeseenNotSettled: 0,
              recreateFattenConfinementTotalAccomplishedNotSettled: 0,
              agricultureTotalForeseenNotSettled: 0,
              agricultureTotalAccomplishedNotSettled: 0,
              othersTotalForeseenNotSettled: 0,
              othersTotalAccomplishedNotSettled: 0,
              allTypesTotalForeseenSettled: 0,
              allTypesTotalAccomplishedSettled: 0,
              growthTotalForeseenSettled: 0,
              growthTotalAccomplishedSettled: 0,
              recreateFattenPastureTotalForeseenSettled: 0,
              recreateFattenPastureTotalAccomplishedSettled: 0,
              recreateFattenSemiConfinementTotalForeseenSettled: 0,
              recreateFattenSemiConfinementTotalAccomplishedSettled: 0,
              recreateFattenConfinementTotalForeseenSettled: 0,
              recreateFattenConfinementTotalAccomplishedSettled: 0,
              agricultureTotalForeseenSettled: 0,
              agricultureTotalAccomplishedSettled: 0,
              othersTotalForeseenSettled: 0,
              othersTotalAccomplishedSettled: 0,
            };
          });
          incomesInPeriod = months.map((month) => {
            const monthFormatted = month < 10 ? `0${month}` : month;
            const monthStart = moment(`${selectedYear}-${monthFormatted}-01`);
            const indexFounded = incomesInPeriod.findIndex((v) =>
              moment(v.movementDate).isSame(monthStart, "month")
            );
            if (indexFounded !== -1) {
              return incomesInPeriod[indexFounded];
            }
            return {
              movementDate: monthStart,
              allTypesTotalForeseen: 0,
              allTypesTotalAccomplished: 0,
              growthTotalForeseen: 0,
              growthTotalAccomplished: 0,
              recreateFattenPastureTotalForeseen: 0,
              recreateFattenPastureTotalAccomplished: 0,
              recreateFattenSemiConfinementTotalForeseen: 0,
              recreateFattenSemiConfinementTotalAccomplished: 0,
              recreateFattenConfinementTotalForeseen: 0,
              recreateFattenConfinementTotalAccomplished: 0,
              agricultureTotalForeseen: 0,
              agricultureTotalAccomplished: 0,
              othersTotalForeseen: 0,
              othersTotalAccomplished: 0,
              allTypesTotalForeseenNotSettled: 0,
              allTypesTotalAccomplishedNotSettled: 0,
              growthTotalForeseenNotSettled: 0,
              growthTotalAccomplishedNotSettled: 0,
              recreateFattenPastureTotalForeseenNotSettled: 0,
              recreateFattenPastureTotalAccomplishedNotSettled: 0,
              recreateFattenSemiConfinementTotalForeseenNotSettled: 0,
              recreateFattenSemiConfinementTotalAccomplishedNotSettled: 0,
              recreateFattenConfinementTotalForeseenNotSettled: 0,
              recreateFattenConfinementTotalAccomplishedNotSettled: 0,
              agricultureTotalForeseenNotSettled: 0,
              agricultureTotalAccomplishedNotSettled: 0,
              othersTotalForeseenNotSettled: 0,
              othersTotalAccomplishedNotSettled: 0,
              allTypesTotalForeseenSettled: 0,
              allTypesTotalAccomplishedSettled: 0,
              growthTotalForeseenSettled: 0,
              growthTotalAccomplishedSettled: 0,
              recreateFattenPastureTotalForeseenSettled: 0,
              recreateFattenPastureTotalAccomplishedSettled: 0,
              recreateFattenSemiConfinementTotalForeseenSettled: 0,
              recreateFattenSemiConfinementTotalAccomplishedSettled: 0,
              recreateFattenConfinementTotalForeseenSettled: 0,
              recreateFattenConfinementTotalAccomplishedSettled: 0,
              agricultureTotalForeseenSettled: 0,
              agricultureTotalAccomplishedSettled: 0,
              othersTotalForeseenSettled: 0,
              othersTotalAccomplishedSettled: 0,
            };
          });
        }

        // Chart Expense Values
        const expenseChartData = expensesInPeriod.map((expense) => {
          let expenseValue = 0;
          if (costType === "AllTypes") {
            expenseValue =
              expense.growthTotalAccomplished +
              expense.recreateFattenPastureTotalAccomplished +
              expense.recreateFattenSemiConfinementTotalAccomplished +
              expense.recreateFattenConfinementTotalAccomplished +
              expense.agricultureTotalAccomplished +
              expense.othersTotalAccomplished;
          } else {
            let costTypeDataIndex = listCostType.find(
              (ct) => ct.costType === costType
            )?.dataIndex;
            expenseValue = expense[costTypeDataIndex];
          }

          return {
            label: moment(expense.movementDate).format("MM/YYYY"),
            y: -expenseValue,
          };
        });

        // Chart Income values
        const incomeChartData = incomesInPeriod.map((income) => {
          let incomeValue = 0;
          if (costType === "AllTypes") {
            incomeValue =
              income.growthTotalAccomplished +
              income.recreateFattenPastureTotalAccomplished +
              income.recreateFattenSemiConfinementTotalAccomplished +
              income.recreateFattenConfinementTotalAccomplished +
              income.agricultureTotalForeseen +
              income.othersTotalForeseen;
          } else {
            let costTypeDataIndex = listCostType.find(
              (ct) => ct.costType === costType
            )?.dataIndex;
            incomeValue = income[costTypeDataIndex];
          }

          return {
            label: moment(income.movementDate).format("MM/YYYY"),
            y: incomeValue,
          };
        });

        // Chart Result and Balance values
        const resultChartData = [];
        const balanceChartData = [];
        let balance = 0;

        for (let index = 0; index < expenseChartData.length; index++) {
          let resultValue =
            expenseChartData[index].y + incomeChartData[index].y;
          balance = balance + resultValue;

          resultChartData.push({
            label: expenseChartData[index].label,
            y: resultValue,
          });
          balanceChartData.push({
            label: expenseChartData[index].label,
            y: balance,
          });
        }

        setExpenseData(expenseChartData);
        setIncomeData(incomeChartData);
        setResultData(resultChartData);
        setBalanceData(balanceChartData);
      }
    }

    if (
      cashFlowData !== null &&
      cashFlowData !== undefined &&
      harvestSelected !== null
    ) {
      generateChart();
    }
  }, [
    cashFlowData,
    costType,
    harvestSelected,
    listHarvests,
    listCostType,
    timeSeriesType,
    selectedYear,
  ]);

  return (
    <Fragment>
      <Container>
        <Row type="flex" align="middle" gutter={16} className="filterRow">
          <Col span={4}>
            <Row>
              <label className="filterLabel">
                {translation.financial.cashFlow.filters.timeExibition}
              </label>
            </Row>
            <Row>
              <Radio.Group
                name="timeSeriesType"
                value={timeSeriesType}
                onChange={(e) => setTimeSeriesType(e.target.value)}
              >
                {TIME_SERIES_TYPE.map((type) => (
                  <RadioButton key={type} value={type}>
                    {
                      translation.financial.cashFlow.filters
                        .timeExibitionOptions[type]
                    }
                  </RadioButton>
                ))}
              </Radio.Group>
            </Row>
            <Row style={{ marginTop: "8px" }}>
              <label className="filterLabel">
                {translation.financial.cashFlow.filters.harvest}
              </label>
            </Row>
            {timeSeriesType === "year" && (
              <Row>
                <Select
                  style={{ width: "100%" }}
                  name="yearSelected"
                  value={selectedYear || undefined}
                  placeholder={translation.defaultSelectPlaceholder}
                  onChange={(value) => setSelectedYear(value)}
                >
                  {renderYears.map((year) => (
                    <Select.Option key={year} value={year}>
                      {year}
                    </Select.Option>
                  ))}
                </Select>
              </Row>
            )}
            {timeSeriesType === "harvest" && (
              <Row>
                <Select
                  style={{ width: "100%" }}
                  name="harvestSelected"
                  value={harvestSelected || undefined}
                  placeholder={translation.defaultSelectPlaceholder}
                  onChange={(value) => setHarvestSelected(value)}
                >
                  {listHarvests.map((h) => (
                    <Select.Option key={h.id} value={h.id}>{`${moment(
                      h.startDate
                    ).format("MM/YYYY")} à ${moment(h.endDate).format(
                      "MM/YYYY"
                    )}`}</Select.Option>
                  ))}
                </Select>
              </Row>
            )}
          </Col>
          <Col span={6}>
            <Row>
              <label className="filterLabel">
                {translation.financial.cashFlow.filters.costType}
              </label>
            </Row>
            <Row>
              <Select
                style={{ width: "100%" }}
                name="costType"
                value={costType || undefined}
                placeholder={translation.defaultSelectPlaceholder}
                onChange={(value) => setCostType(value)}
              >
                <Select.Option key="AllTypes" value="AllTypes">
                  Todos
                </Select.Option>
                {listCostType.map((l) => (
                  <Select.Option key={l.costType} value={l.costType}>
                    {`${translation.costType[l.costType]}`}
                  </Select.Option>
                ))}
              </Select>
            </Row>
          </Col>
        </Row>
        <Spin spinning={isLoadingListFinancialCashFlow}>
          <CanvasJSChart options={options} />
        </Spin>
      </Container>
    </Fragment>
  );
};

export default StackedColumnBarChart;
